import React, { useState, useEffect, useCallback } from "react";
import { fetchData, getOrCreatePlayerForUser } from "../utils/api.js";
import PropTypes from "prop-types";
import "./TeamRoster.css";
import "./common/MemberStyles.css";
import "./common/SharedTable.css";
import ConfirmationModal from "./common/ConfirmationModal.js";
import { Drawer } from "./common/Drawer.js";

import { formatPhoneNumber, PersonList } from "./common/PersonList.js";
import { BaseballIcon } from "./common/Icons.js";
import { Button } from "./ui/Button";

const TeamRoster = ({ team, isCoach, user }) => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [playerToRemove, setPlayerToRemove] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState(["player"]);

  const [newPlayer, setNewPlayer] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
  });
  const [error, setError] = useState("");

  const fetchPlayers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchData(
        `/roster_details?team_id=eq.${team.team_id || team.id}`,
      );
      setPlayers(data || []);
    } catch (err) {
      console.error("Error fetching players:", err);
    } finally {
      setLoading(false);
    }
  }, [team.id, team.team_id]);

  useEffect(() => {
    fetchPlayers();
  }, [fetchPlayers]);

  const handleSavePlayer = async (e) => {
    e.preventDefault();
    setError("");

    if (!newPlayer.first_name.trim() || !newPlayer.last_name.trim()) {
      setError("First Name and Last Name are required.");
      return;
    }
    const isValidEmail = (email) => {
      const str = String(email || "").trim();
      const atIdx = str.indexOf("@");
      const dotIdx = str.lastIndexOf(".");
      return (
        !str.includes(" ") &&
        atIdx > 0 &&
        dotIdx > atIdx + 1 &&
        dotIdx < str.length - 1
      );
    };
    if (newPlayer.email && !isValidEmail(newPlayer.email)) {
      setError("Please enter a valid email address.");
      return;
    }

    try {
      const is_scorer = selectedRoles.includes("scorer");
      const nonScorerRoles = selectedRoles.filter((r) => r !== "scorer");
      let primaryRole = "player";
      if (nonScorerRoles.length > 0) {
        primaryRole = nonScorerRoles.join(", ");
      } else if (is_scorer) {
        primaryRole = "scorer";
      }

      if (editingPlayer) {
        // Update existing player
        const result = await fetchData(
          `/people?person_id=eq.${editingPlayer.person_id}`,
          {
            method: "PATCH",
            headers: { Prefer: "return=representation" },
            body: JSON.stringify({
              first_name: newPlayer.first_name,
              last_name: newPlayer.last_name,
              email: newPlayer.email || null,
              phone: newPlayer.phone_number || newPlayer.phone || null,
            }),
          },
        );

        if (result && result.length > 0) {
          // Merge with roster details manually to update UI
          const updatedPlayer = { ...editingPlayer, ...result[0] };

          await fetchData(
            `/team_rosters?team_id=eq.${team.team_id || team.id}&person_id=eq.${editingPlayer.person_id}`,
            {
              method: "PATCH",
              body: JSON.stringify({
                role: primaryRole,
                is_scorer: is_scorer,
              }),
            },
          );
          updatedPlayer.role = primaryRole;
          updatedPlayer.is_scorer = is_scorer;

          setPlayers(
            players.map((p) =>
              p.person_id === editingPlayer.person_id ? updatedPlayer : p,
            ),
          );
        }
      } else {
        // Add new player
        const playerResult = await fetchData(`/people`, {
          method: "POST",
          headers: { Prefer: "return=representation" },
          body: JSON.stringify({
            first_name: newPlayer.first_name,
            last_name: newPlayer.last_name,
            email: newPlayer.email || null,
            phone: newPlayer.phone_number || newPlayer.phone || null,
          }),
        });

        if (playerResult && playerResult.length > 0) {
          const insertedPlayer = playerResult[0];
          await fetchData(`/team_rosters`, {
            method: "POST",
            body: JSON.stringify({
              team_id: team.team_id || team.id,
              person_id: insertedPlayer.person_id,
              role: primaryRole,
              is_scorer: is_scorer,
            }),
          });

          setPlayers([
            ...players,
            {
              ...insertedPlayer,
              team_id: team.team_id || team.id,
              role: primaryRole,
              is_scorer: is_scorer,
            },
          ]);
        }
      }

      setShowAddModal(false);
      setEditingPlayer(null);
      resetForm();
    } catch (err) {
      console.error("Error saving player:", err);
      setError("Failed to save player. " + err.message);
    }
  };

  const confirmRemovePlayer = (player) => {
    setPlayerToRemove(player);
  };

  const cancelRemovePlayer = () => {
    setPlayerToRemove(null);
  };

  const executeRemovePlayer = async () => {
    if (!playerToRemove) return;
    const playerId = playerToRemove.person_id;
    try {
      await fetchData(
        `/team_rosters?team_id=eq.${team.team_id || team.id}&person_id=eq.${playerId}`,
        {
          method: "DELETE",
        },
      );
      setPlayers((prev) => prev.filter((p) => p.person_id !== playerId));
      setPlayerToRemove(null);
    } catch (err) {
      console.error("Error removing player:", err);
      setError("Network error");
      setPlayerToRemove(null);
    }
  };

  const resetForm = () => {
    setNewPlayer({
      first_name: "",
      last_name: "",
      email: "",
      phone_number: "",
    });
    setSelectedRoles(["player"]);
  };

  const openAddModal = () => {
    setEditingPlayer(null);
    resetForm();
    setShowAddModal(true);
  };

  const openEditModal = (player) => {
    setEditingPlayer(player);
    setNewPlayer({
      first_name: player.first_name || "",
      last_name: player.last_name || "",
      email: player.email || "",
      phone_number: formatPhoneNumber(
        player.phone_number || player.phone || "",
      ),
    });
    const roles = [];
    const rLower = (player.role || "").toLowerCase();
    if (rLower.includes("player")) roles.push("player");
    if (rLower.includes("coach") || rLower.includes("captain"))
      roles.push("coach");
    if (rLower.includes("scorer") || player.is_scorer) roles.push("scorer");
    if (rLower.includes("fan")) roles.push("fan");
    if (roles.length === 0) roles.push("player");
    setSelectedRoles(roles);
    setShowAddModal(true);
  };

  const addCoachAsPlayer = async () => {
    try {
      const coachPlayerId = await getOrCreatePlayerForUser(user);

      if (coachPlayerId) {
        await fetchData(`/team_rosters`, {
          method: "POST",
          body: JSON.stringify({
            team_id: team.team_id || team.id,
            person_id: coachPlayerId,
            role: "coach",
          }),
        });

        // Refresh roster
        fetchPlayers();
      }
    } catch (err) {
      console.error("Error adding coach as player:", err);
      setError("Network error. " + err.message);
    }
  };

  const handleAddMe = async () => {
    await addCoachAsPlayer();
  };

  const currentUserId = user ? user.uid || user.id : null;
  const rosterPlayers = players.filter(
    (p) => (p.role || "").toLowerCase().trim() !== "fan",
  );

  let content;
  if (loading) {
    content = <div className="loading-spinner" />;
  } else if (rosterPlayers.length === 0) {
    content = <div className="empty-state">No members added yet.</div>;
  } else {
    content = (
      <PersonList
        people={[...rosterPlayers]
          .sort((a, b) => {
            const coachId = team?.coach_id;
            const aIsCoach = a.user_id && a.user_id === coachId;
            const bIsCoach = b.user_id && b.user_id === coachId;
            const aIsMe =
              currentUserId &&
              (a.user_id === currentUserId ||
                a.uid === currentUserId ||
                (a.email &&
                  user?.email &&
                  a.email.toLowerCase() === user.email.toLowerCase()));
            const bIsMe =
              currentUserId &&
              (b.user_id === currentUserId ||
                b.uid === currentUserId ||
                (b.email &&
                  user?.email &&
                  b.email.toLowerCase() === user.email.toLowerCase()));

            if (aIsCoach && !bIsCoach) return -1;
            if (!aIsCoach && bIsCoach) return 1;
            if (aIsMe && !bIsMe) return -1;
            if (!aIsMe && bIsMe) return 1;
            return 0;
          })
          .map((player) => ({
            ...player,
            canRemove:
              !team?.coach_id ||
              String(player.user_id) !== String(team.coach_id),
          }))}
        type="Member"
        onEdit={openEditModal}
        onRemove={confirmRemovePlayer}
        canEdit={isCoach}
        showContactColumn={Boolean(isCoach)}
        renderRoleColumn={(person) => {
          const rLower = (person.role || "").toLowerCase();
          const isMemberCoach =
            rLower.includes("coach") ||
            rLower.includes("captain") ||
            Boolean(
              team?.coach_id &&
              person.user_id &&
              String(person.user_id) === String(team.coach_id),
            );
          const isScorer = Boolean(
            person.is_scorer || rLower.includes("scorer"),
          );
          if (!isMemberCoach && !isScorer) return null;
          return (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                color: "var(--color-text-secondary)",
                fontSize: "0.85rem",
              }}
            >
              {isMemberCoach && (
                <BaseballIcon size="xs" title="Coach" alt="Coach" />
              )}
              {isScorer && <span>Scorer</span>}
            </span>
          );
        }}
        emptyMessage="No members added yet."
      />
    );
  }

  let isUserOnRoster = false;
  if (user) {
    const uid = user.firebaseUid || user.id || user.uid;
    const email = user.email ? user.email.toLowerCase() : "";
    isUserOnRoster = rosterPlayers.some((p) => {
      if (p.user_id && uid && p.user_id === uid) return true;
      if (p.email && email && p.email.toLowerCase() === email) return true;
      return false;
    });
  }

  return (
    <div>
      {isCoach && (
        <div
          className="admin-row-header"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "0.75rem",
          }}
        >
          <div style={{ display: "flex", gap: "10px" }}>
            {!isUserOnRoster && (
              <Button
                type="button"
                className="chip-btn"
                onClick={handleAddMe}
                disabled={!user}
              >
                Add Me
              </Button>
            )}
            <Button variant="primary" type="button" onClick={openAddModal}>
              Add Member
            </Button>
          </div>
        </div>
      )}

      {error && !showAddModal && <div className="error-banner">{error}</div>}

      {content}

      <Drawer
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={editingPlayer ? "Edit Member" : "Add Member"}
      >
        {error && <p className="error-text">{error}</p>}
        <form onSubmit={handleSavePlayer} className="form-compact">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="player-first-name">First Name</label>
              <input
                id="player-first-name"
                type="text"
                value={newPlayer.first_name}
                onChange={(e) =>
                  setNewPlayer({ ...newPlayer, first_name: e.target.value })
                }
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="player-last-name">Last Name</label>
              <input
                id="player-last-name"
                type="text"
                value={newPlayer.last_name}
                onChange={(e) =>
                  setNewPlayer({ ...newPlayer, last_name: e.target.value })
                }
                required
              />
            </div>
          </div>
          <div className="form-group full-width">
            <label htmlFor="player-email">Email (Optional)</label>
            <input
              id="player-email"
              type="email"
              value={newPlayer.email}
              onChange={(e) =>
                setNewPlayer({ ...newPlayer, email: e.target.value })
              }
              placeholder="e.g. player@example.com"
            />
            <small className="help-text">
              Enter email to invite user or link existing account.
            </small>
          </div>
          <div className="form-group full-width">
            <label htmlFor="player-phone">Phone (Optional)</label>
            <input
              id="player-phone"
              type="tel"
              value={newPlayer.phone_number}
              onChange={(e) =>
                setNewPlayer({
                  ...newPlayer,
                  phone_number: formatPhoneNumber(e.target.value),
                })
              }
              placeholder="e.g. (555) 555-5555"
            />
          </div>
          <div
            className="form-group full-width"
            style={{ marginTop: "0.5rem" }}
          >
            <span
              className="form-label"
              style={{ marginBottom: "8px", display: "block", fontWeight: 600 }}
            >
              Roles
            </span>
            <div
              style={{
                display: "flex",
                gap: "16px",
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              {[
                { key: "player", label: "Player" },
                { key: "coach", label: "Coach" },
                { key: "scorer", label: "Scorer" },
                { key: "fan", label: "Fan" },
              ].map(({ key, label }) => (
                <label
                  key={key}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    cursor: "pointer",
                    fontSize: "0.95rem",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedRoles.includes(key)}
                    onChange={() => {
                      setSelectedRoles((prev) =>
                        prev.includes(key)
                          ? prev.filter((r) => r !== key)
                          : [...prev, key],
                      );
                    }}
                    style={{ width: "auto" }}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="modal-actions" style={{ marginTop: "1.5rem" }}>
            <Button
              variant="outline"
              type="button"

              onClick={() => setShowAddModal(false)}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingPlayer ? "Save Changes" : "Add Member"}
            </Button>
          </div>
        </form>
      </Drawer>

      {playerToRemove && (
        <ConfirmationModal
          isOpen={!!playerToRemove}
          onClose={cancelRemovePlayer}
          onConfirm={executeRemovePlayer}
          title="Remove Member"
        >
          Are you sure you want to remove{" "}
          <strong>
            {playerToRemove.first_name} {playerToRemove.last_name}
          </strong>
          {"?"}
        </ConfirmationModal>
      )}
    </div>
  );
};

TeamRoster.propTypes = {
  team: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    coach_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    team_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    city: PropTypes.string,
    state: PropTypes.string,
  }).isRequired,
  isCoach: PropTypes.bool,
  user: PropTypes.shape({
    id: PropTypes.string,
    uid: PropTypes.string,
    firebaseUid: PropTypes.string,
    getIdToken: PropTypes.func,
    name: PropTypes.string,
    displayName: PropTypes.string,
    email: PropTypes.string,
    phone_number: PropTypes.string,
  }),
};

export default TeamRoster;
